import { QUEUE_ENTRY_STATUSES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, ageSchema, reasonSchema } from './clinical-common.ts';
import { SERVICE_POINT_KINDS } from './service-points.ts';

// Queue boards, moving patients between queues, and the public display
// (docs/plans/phase1-triage.md §2.8–2.10, §3.3, §6.1–6.2).

export { QUEUE_ENTRY_STATUSES };

export const servicePointQueueSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(SERVICE_POINT_KINDS),
  /** Patients waiting or called, not yet being seen. */
  waiting: z.number().int().min(0),
  /** Whole minutes the longest-waiting patient has waited; null when nobody waits. */
  longestWaitMinutes: z.number().int().min(0).nullable(),
});

export type ServicePointQueueView = z.infer<typeof servicePointQueueSchema>;

export const queueCategorySchema = z.object({
  system: z.string(),
  code: z.string(),
  display: z.string(),
  /** Not yet approved by the clinical lead; the screen says "Provisional". */
  provisional: z.boolean(),
});

const queueBoardEntryCommon = {
  id: z.uuid(),
  /** The daily ticket, shown in mono; null for visits opened before tickets existed. */
  ticket: z.string().nullable(),
  status: z.enum(QUEUE_ENTRY_STATUSES),
  enteredAt: z.iso.datetime(),
  calledAt: z.iso.datetime().nullable(),
  calledBy: actorSchema.nullable(),
  callCount: z.number().int().min(0),
  waitingMinutes: z.number().int().min(0),
  /**
   * An emergency (casualty) visit: seen first within its triage rank, and the row says
   * "Emergency" (docs/plans/phase2-casualty.md §1.1 item 5).
   */
  emergency: z.boolean(),
};

/**
 * A queue place. A restricted one is on a visit whose sensitivity labels the reader's roles
 * aren't granted: it keeps its ticket, wait and status, so it can still be called and moved,
 * but shows no patient, visit, triage category, flags or note ("Restricted record").
 */
export const queueBoardEntrySchema = z.discriminatedUnion('restricted', [
  z.object({
    ...queueBoardEntryCommon,
    restricted: z.literal(false),
    visitId: z.uuid(),
    patientId: z.uuid(),
    patient: z.object({
      displayName: z.string(),
      /** Short sex display, e.g. "F", from the value set. */
      sex: z.string(),
      age: ageSchema.nullable(),
      ageEstimated: z.boolean(),
      patientNumber: z.string(),
    }),
    /** The current triage category, once triaged. */
    category: queueCategorySchema.nullable(),
    /** Why a patient moved up, in words: the category and any priority flags. */
    priorityFlags: z.array(z.object({ code: z.string(), display: z.string() })),
    /** A short note from the room that sent the patient. Never shown on the display. */
    transferNote: z.string().nullable(),
  }),
  z.object({
    ...queueBoardEntryCommon,
    restricted: z.literal(true),
    visitId: z.null(),
    patientId: z.null(),
    patient: z.null(),
    category: z.null(),
    priorityFlags: z.array(z.object({ code: z.string(), display: z.string() })).length(0),
    transferNote: z.null(),
  }),
]);

export type QueueBoardEntryView = z.infer<typeof queueBoardEntrySchema>;

export const queueBoardSchema = z.object({
  servicePoint: servicePointQueueSchema,
  /** Active entries in the order patients are seen (`orderQueue` in @wodi/domain). */
  entries: z.array(queueBoardEntrySchema),
});

export type QueueBoardView = z.infer<typeof queueBoardSchema>;

export const transferQueueEntryRequestSchema = z.object({
  toServicePointId: z.uuid(),
  note: z.string().trim().min(1).max(200).optional(),
});

export const cancelQueueEntryRequestSchema = z.object({ reason: reasonSchema });

/** Priority flags (plan P2): codes from the `queue_priority_flag` value set; empty clears them. */
export const setQueueFlagsRequestSchema = z.object({
  flags: z.array(z.string().trim().min(1).max(64)).max(10),
});

/** After a transfer: the ended entry and the new one. */
export const transferResultSchema = z.object({
  ended: z.object({ id: z.uuid(), status: z.enum(QUEUE_ENTRY_STATUSES) }),
  created: z.object({ id: z.uuid(), servicePointId: z.uuid() }),
});

// --- Public display (plan §3.4, §6.2) ------------------------------------------------------

/**
 * What a waiting-area display receives: ticket numbers and service point names only.
 * No names, patient numbers, ages, categories or IDs of patients or visits. A test
 * asserts the response carries nothing else.
 */
export const displayBoardSchema = z.object({
  displayName: z.string(),
  calling: z.array(z.object({ ticket: z.string(), servicePoint: z.string() })),
  waiting: z.array(z.object({ servicePoint: z.string(), tickets: z.array(z.string()) })),
  generatedAt: z.iso.datetime(),
});

export type DisplayBoardView = z.infer<typeof displayBoardSchema>;

export const queueDisplaySchema = z.object({
  id: z.uuid(),
  facilityId: z.uuid(),
  name: z.string(),
  servicePointIds: z.array(z.uuid()),
  status: z.enum(['active', 'revoked']),
  lastSeenAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type QueueDisplayView = z.infer<typeof queueDisplaySchema>;

export const queueDisplayInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  servicePointIds: z.array(z.uuid()).min(1).max(20),
});

export const updateQueueDisplayRequestSchema = queueDisplayInputSchema.partial();

/** Returned once, at pairing: the link opened on the display. The token is never stored. */
export const pairedQueueDisplaySchema = z.object({
  display: queueDisplaySchema,
  pairingToken: z.string(),
});

export const displayPairRequestSchema = z.object({ token: z.string().min(20).max(200) });
